import classNames from "classnames";
import React, { RefAttributes } from "react";
import { TextTemplateKey } from "../types";
import { LocalizedText } from "./localized-text";
import { CSS_CLASSES } from "./text-css-classes";
import { LineClampOption, TextColorOption, TextProps, TypographicVariants } from "./text.types";

/**
 *
 */
const variantToTagMap: {
  [key in TypographicVariants]: keyof Pick<JSX.IntrinsicElements, "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "div">;
} = {
  "heading-01": "h1",
  "heading-02": "h2",
  "heading-03": "h3",
  "heading-04": "h4",
  "heading-05": "h5",
  "heading-06": "h6",
  "body-long": "div",
  "body-short": "div",
  "helper-text": "div",
  caption: "div",
  label: "div",
};

/**
 * This component represent the core Typography component of the Blueprint Design System.
 *
 * It forms a core part of the text Internationalization/Localization support that's built in to the library.
 *
 * Consuming application should always use this component whenever they wish to render any text on the UI, instead of
 * adding free text.
 */
export const Text: <T extends TextTemplateKey = TextTemplateKey>(
  props: TextProps<T> & RefAttributes<HTMLHeadingElement | HTMLDivElement>
) => React.ReactElement = React.memo(
  React.forwardRef(
    (
      {
        //  Own props
        id,
        variant = "body-long",
        color,
        lineClamp,
        as,
        children,
        useChildrenAsFallback,
        className: classNameProp,
        // Text to LocalizedText passthrough props
        textTemplateKey,
        textTemplateData,
        withTenant,
        ...restProps
      },
      ref
    ) => {
      const TagName = as ?? variantToTagMap[variant];
      return (
        <TagName
          id={id}
          className={computeClassName(variant, color, lineClamp, classNameProp)}
          ref={ref}
          {...restProps}
        >
          {useChildrenAsFallback ? (
            textTemplateKey ? (
              <LocalizedText
                textTemplateKey={textTemplateKey}
                textTemplateData={textTemplateData}
                withTenant={withTenant}
                defaultText={children}
              />
            ) : (
              children
            )
          ) : children ? (
            children
          ) : textTemplateKey ? (
            <LocalizedText
              textTemplateKey={textTemplateKey}
              textTemplateData={textTemplateData}
              withTenant={withTenant}
            />
          ) : null}
        </TagName>
      );
    }
  )
);
(Text as any).displayName = "Text";

/**
 * This function will compute the CSS class to be applied for given styling related props
 */
const computeClassName = (
  variant: TypographicVariants = "body-long",
  color?: TextColorOption,
  lineClamp?: LineClampOption,
  className?: string
) => {
  return classNames(
    {
      [CSS_CLASSES.TEXT_HEADING_01]: variant === "heading-01",
      [CSS_CLASSES.TEXT_HEADING_02]: variant === "heading-02",
      [CSS_CLASSES.TEXT_HEADING_03]: variant === "heading-03",
      [CSS_CLASSES.TEXT_HEADING_04]: variant === "heading-04",
      [CSS_CLASSES.TEXT_HEADING_05]: variant === "heading-05",
      [CSS_CLASSES.TEXT_HEADING_06]: variant === "heading-06",
      [CSS_CLASSES.TEXT_BODY_SHORT]: variant === "body-short",
      [CSS_CLASSES.TEXT_BODY_LONG]: variant === "body-long",
      [CSS_CLASSES.TEXT_CAPTION]: variant === "caption",
      [CSS_CLASSES.TEXT_LABEL]: variant === "label",
      [CSS_CLASSES.TEXT_HELPER_TEXT]: variant === "helper-text",

      [CSS_CLASSES.TEXT_PRIMARY]: color === "primary",
      [CSS_CLASSES.TEXT_SECONDARY]: color === "secondary",
      [CSS_CLASSES.TEXT_INTERACTIVE]: color === "interactive",
      [CSS_CLASSES.TEXT_ACCENT]: color === "accent",
      [CSS_CLASSES.TEXT_GRAY]: color === "gray",
      [CSS_CLASSES.TEXT_ERROR]: color === "error",
      [CSS_CLASSES.TEXT_WARNING]: color === "warning",
      [CSS_CLASSES.TEXT_SUCCESS]: color === "success",
      [CSS_CLASSES.TEXT_WHITE]: color === "white",
      [CSS_CLASSES.TEXT_BLACK]: color === "black",
      [CSS_CLASSES.TEXT_CURRENTCOLOR]: color === "currentcolor",

      "line-clamp-1": lineClamp === 1,
      "line-clamp-2": lineClamp === 2,
      "line-clamp-3": lineClamp === 3,
      "line-clamp-4": lineClamp === 4,
      "line-clamp-5": lineClamp === 5,
      "line-clamp-6": lineClamp === 6,
      "line-clamp-none": lineClamp === undefined || lineClamp === null,
    },
    className
  );
};
